// PasskeyEnrollPrompt — post-login nudge to set up biometric sign-in, shown ONLY
// to a user who just tried "Sign in with a passkey", had none, and fell back to
// a password (LoginModal sets `sessionStorage.passkeyIntent`). Requires a
// WebAuthn-capable browser and no existing passkey. Tapping "Enable" fires the
// device biometric prompt (Face ID / fingerprint / Windows Hello) and registers
// a passkey, so the next login can use "Sign in with a passkey". "Not now" is
// remembered per-account on this device; users who never click the passkey
// button are never nudged.
import React, { useState, useEffect } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import { usePasskeys } from '../../hooks/usePasskeys';
import { isMobileDevice } from '../../utils/device';
import FingerprintIcon from './FingerprintIcon';
import './PasskeyEnrollPrompt.css';

const dismissKey = (email) => `passkeyEnrollDismissed:${email || 'unknown'}`;

const PasskeyEnrollPrompt = () => {
  const { auth, userEmail } = useFileContext();
  const { supported, credentials, enroll, refresh } = usePasskeys();

  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Re-check the user's passkeys once they're authenticated (listing needs auth).
  useEffect(() => {
    let cancelled = false;
    if (auth && supported) {
      setReady(false);
      refresh().finally(() => {
        if (!cancelled) setReady(true);
      });
    } else {
      setReady(false);
      setVisible(false);
    }
    return () => { cancelled = true; };
  }, [auth, supported, refresh]);

  // Decide whether to show the nudge (only after the passkey check has resolved,
  // so users who already have a passkey never see a flash).
  useEffect(() => {
    if (done) return; // keep the success state on screen until its timeout hides it
    if (!ready || !auth || !supported || !isMobileDevice()) { setVisible(false); return; } // passkeys: mobile only
    if (credentials.length > 0) { setVisible(false); return; } // already has a passkey
    // Only nudge if the user actually tried "Sign in with a passkey" this session.
    let intent = false;
    try { intent = sessionStorage.getItem('passkeyIntent') === '1'; } catch (e) { /* ignore */ }
    if (!intent) { setVisible(false); return; }
    let dismissed = false;
    try { dismissed = !!localStorage.getItem(dismissKey(userEmail)); } catch (e) { /* ignore */ }
    if (dismissed) { setVisible(false); return; }
    // One-shot: consume the intent so this nudge shows once per passkey attempt.
    try { sessionStorage.removeItem('passkeyIntent'); } catch (e) { /* ignore */ }
    setVisible(true);
  }, [ready, auth, supported, credentials, userEmail, done]);

  const handleEnable = async () => {
    setError('');
    setBusy(true);
    try {
      await enroll();
      setDone(true);
      setTimeout(() => { setVisible(false); setDone(false); }, 1800);
    } catch (err) {
      console.warn('Passkey enroll prompt failed:', err);
      const name = err?.name || 'Error';
      const detail = `${name}: ${err?.message || 'unknown'}`;
      if (name === 'NotAllowedError' || /cancel|timed out|not allowed/i.test(err?.message || '')) {
        setError(`Cancelled or blocked by the browser. On Android, Chrome is most reliable — Opera’s passkey support is limited. [${detail}]`);
      } else if (name === 'NotSupportedError' || name === 'SecurityError' || name === 'AbortError') {
        setError(`This browser couldn’t create a passkey here. On Android, try Chrome. [${detail}]`);
      } else {
        setError(`Couldn’t enable biometric sign-in. [${detail}]`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleNotNow = () => {
    try { localStorage.setItem(dismissKey(userEmail), String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="passkey-prompt" role="dialog" aria-label="Enable biometric sign-in">
      <div className="passkey-prompt__icon" aria-hidden="true">
        <FingerprintIcon />
      </div>

      {done ? (
        <div className="passkey-prompt__body">
          <p className="passkey-prompt__title">Biometric sign-in is on ✓</p>
          <p className="passkey-prompt__subtitle">Next time, tap “Sign in with a passkey”.</p>
        </div>
      ) : (
        <>
          <div className="passkey-prompt__body">
            <p className="passkey-prompt__title">Sign in faster next time</p>
            <p className="passkey-prompt__subtitle">
              Use Face ID, your fingerprint, or Windows Hello to sign in — no password or code needed.
            </p>
            {error && <p className="passkey-prompt__error">{error}</p>}
          </div>
          <div className="passkey-prompt__actions">
            <button type="button" className="passkey-prompt__enable" onClick={handleEnable} disabled={busy}>
              {busy ? 'Waiting for biometric…' : 'Enable biometric sign-in'}
            </button>
            <button type="button" className="passkey-prompt__later" onClick={handleNotNow} disabled={busy}>
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PasskeyEnrollPrompt;
